// Extended mock data for blog posts
export const blogPosts = [
  {
    id: 1,
    title: "The Future of AI in Software Development",
    slug: "future-ai-software-development",
    excerpt:
      "Exploring how artificial intelligence is revolutionizing the way we build applications and transforming the developer experience.",
    content: `
      <h2>Introduction</h2>
      <p>Artificial Intelligence is no longer a futuristic concept—it's reshaping software development today. From code generation to automated testing, AI is becoming an indispensable tool in the modern developer's toolkit.</p>

      <h2>AI-Powered Code Generation</h2>
      <p>Tools like GitHub Copilot and ChatGPT have demonstrated that AI can write production-ready code. But the real revolution is in how these tools augment human creativity rather than replace it.</p>

      <h2>The Developer Experience</h2>
      <p>AI doesn't just write code—it helps debug, optimize, and document. This shift allows developers to focus on architecture and business logic while AI handles repetitive tasks.</p>

      <h2>Looking Ahead</h2>
      <p>The future holds even more promise: AI-driven architecture decisions, automated performance optimization, and intelligent code reviews that learn from your team's patterns.</p>
    `,
    category: "AI & Machine Learning",
    tags: ["AI", "Development", "Future Tech"],
    author: "Sarah Chen",
    authorRole: "AI Research Lead",
    readTime: 8,
    image:
      "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&h=800&fit=crop",
    publishedDate: "2025-03-15",
    views: 2847,
  },
  {
    id: 2,
    title: "Building Scalable Web Applications with Modern Architecture",
    slug: "scalable-web-applications-modern-architecture",
    excerpt:
      "A deep dive into microservices, serverless, and event-driven architectures for building applications that scale to millions of users.",
    content: `
      <h2>Why Architecture Matters</h2>
      <p>In the age of global applications serving millions of users, architecture decisions made early can make or break your product's success.</p>

      <h2>Microservices vs Monoliths</h2>
      <p>While monoliths aren't dead, microservices offer unparalleled flexibility for teams building at scale. Learn when to use each approach.</p>

      <h2>Serverless: The Next Evolution</h2>
      <p>Serverless computing isn't just a buzzword—it's a paradigm shift that reduces operational overhead while improving scalability.</p>
    `,
    category: "Web Development",
    tags: ["Architecture", "Scalability", "Microservices"],
    author: "Marcus Rodriguez",
    authorRole: "Solutions Architect",
    readTime: 12,
    image:
      "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1200&h=800&fit=crop",
    publishedDate: "2025-03-10",
    views: 3521,
  },
  {
    id: 3,
    title: "Mobile-First Design: Creating Apps Users Love",
    slug: "mobile-first-design-apps-users-love",
    excerpt:
      "Best practices for designing intuitive, beautiful mobile applications that prioritize user experience and performance.",
    content: `
      <h2>The Mobile-First Mindset</h2>
      <p>Designing for mobile isn't about shrinking desktop interfaces—it's about reimagining user experiences for touch-first, attention-limited contexts.</p>

      <h2>Performance is User Experience</h2>
      <p>Every millisecond counts. Learn how to optimize your mobile app for instant loading and smooth interactions.</p>

      <h2>Design Systems at Scale</h2>
      <p>Building a cohesive design system ensures consistency across iOS, Android, and web while accelerating development.</p>
    `,
    category: "UI/UX Design",
    tags: ["Mobile", "Design", "UX"],
    author: "Emily Watson",
    authorRole: "Principal Designer",
    readTime: 10,
    image:
      "https://images.unsplash.com/photo-1592478411213-6153e4ebc07d?w=1200&h=800&fit=crop",
    publishedDate: "2025-03-05",
    views: 1923,
  },
  {
    id: 4,
    title: "Blockchain Beyond Crypto: Real-World Applications",
    slug: "blockchain-real-world-applications",
    excerpt:
      "Discover how blockchain technology is solving real business problems beyond cryptocurrency and financial services.",
    content: `
      <h2>Beyond the Hype</h2>
      <p>Blockchain's true potential lies not in speculation, but in solving real-world trust and verification challenges.</p>

      <h2>Supply Chain Revolution</h2>
      <p>From farm to table, blockchain creates immutable records that ensure authenticity and prevent fraud.</p>

      <h2>Digital Identity</h2>
      <p>Self-sovereign identity solutions built on blockchain give users control over their personal data.</p>
    `,
    category: "Blockchain",
    tags: ["Blockchain", "Enterprise", "Innovation"],
    author: "David Kim",
    authorRole: "Blockchain Engineer",
    readTime: 9,
    image:
      "https://images.unsplash.com/photo-1639322537228-f710d846310a?w=1200&h=800&fit=crop",
    publishedDate: "2025-02-28",
    views: 2156,
  },
  {
    id: 5,
    title: "The Art of API Design: Building Developer-Friendly Interfaces",
    slug: "art-api-design-developer-friendly",
    excerpt:
      "Learn the principles of designing APIs that developers love to use, from RESTful best practices to GraphQL optimization.",
    content: `
      <h2>APIs as Products</h2>
      <p>Great APIs are products in themselves. They need documentation, versioning, and a focus on developer experience.</p>

      <h2>REST vs GraphQL</h2>
      <p>Understanding when to use each paradigm can dramatically improve your API's usability and performance.</p>

      <h2>Security First</h2>
      <p>Authentication, rate limiting, and encryption aren't afterthoughts—they're core to API design.</p>
    `,
    category: "Software Development",
    tags: ["API", "REST", "GraphQL"],
    author: "Alex Turner",
    authorRole: "Backend Architect",
    readTime: 11,
    image:
      "https://images.unsplash.com/photo-1518770660439-4636190af475?w=1200&h=800&fit=crop",
    publishedDate: "2025-02-20",
    views: 2891,
  },
  {
    id: 6,
    title: "Cloud-Native Development: Kubernetes in Production",
    slug: "cloud-native-kubernetes-production",
    excerpt:
      "A practical guide to deploying and managing containerized applications at scale using Kubernetes.",
    content: `
      <h2>Container Orchestration</h2>
      <p>Kubernetes has become the de facto standard for container orchestration, but mastering it requires understanding its core concepts.</p>

      <h2>Production Best Practices</h2>
      <p>From health checks to auto-scaling, learn the patterns that keep production Kubernetes clusters reliable.</p>

      <h2>Cost Optimization</h2>
      <p>Cloud costs can spiral quickly. Discover strategies for running Kubernetes efficiently without sacrificing performance.</p>
    `,
    category: "Cloud Architecture",
    tags: ["Kubernetes", "DevOps", "Cloud"],
    author: "Priya Sharma",
    authorRole: "DevOps Lead",
    readTime: 14,
    image:
      "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1200&h=800&fit=crop",
    publishedDate: "2025-02-15",
    views: 3204,
  },
];

export const blogCategories = [
  "All Posts",
  "AI & Machine Learning",
  "Web Development",
  "Mobile Development",
  "UI/UX Design",
  "Blockchain",
  "Software Development",
  "Cloud Architecture",
];

// Pricing tiers
export const pricingPlans = [
  {
    id: 1,
    name: "Starter",
    tagline: "Perfect for MVPs",
    price: "$15,000",
    priceDetail: "one-time",
    description:
      "Launch your idea with a professional web or mobile application",
    features: [
      "Custom Design (up to 5 pages)",
      "Responsive Web Development",
      "Basic SEO Optimization",
      "Content Management System",
      "3 Months Support",
      "Mobile Responsive",
      "Performance Optimization",
      "Basic Analytics Integration",
    ],
    highlighted: false,
    cta: "Get Started",
    deliveryTime: "4-6 weeks",
  },
  {
    id: 2,
    name: "Professional",
    tagline: "For Growing Businesses",
    price: "$45,000",
    priceDetail: "one-time",
    description: "Full-featured applications with advanced functionality",
    features: [
      "Everything in Starter",
      "Custom Web/Mobile Application",
      "Advanced UI/UX Design",
      "API Development & Integration",
      "User Authentication System",
      "Payment Gateway Integration",
      "Admin Dashboard",
      "6 Months Support & Maintenance",
      "Advanced Analytics",
      "Cloud Deployment (AWS/GCP)",
      "Database Design & Optimization",
      "Security Audit",
    ],
    highlighted: true,
    cta: "Most Popular",
    deliveryTime: "8-12 weeks",
  },
  {
    id: 3,
    name: "Enterprise",
    tagline: "Custom Solutions",
    price: "Custom",
    priceDetail: "contact us",
    description: "Enterprise-grade solutions built for scale",
    features: [
      "Everything in Professional",
      "Microservices Architecture",
      "Multi-platform Development",
      "AI/ML Integration",
      "Blockchain Solutions",
      "Real-time Features",
      "Advanced Security & Compliance",
      "Load Balancing & Auto-scaling",
      "12 Months Support",
      "Dedicated Team",
      "24/7 Monitoring",
      "Custom Integrations",
      "Performance SLA",
      "White-label Solutions",
    ],
    highlighted: false,
    cta: "Contact Sales",
    deliveryTime: "12+ weeks",
  },
];

// Live projects data
export const liveProjects = [
  {
    id: 1,
    name: "FinTech Dashboard",
    client: "Confidential",
    status: "In Development",
    progress: 75,
    tech: ["React", "Node.js", "PostgreSQL", "AWS"],
    description:
      "Real-time financial analytics platform processing 1M+ transactions daily",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop",
    startDate: "Jan 2025",
    expectedLaunch: "Apr 2025",
  },
  {
    id: 2,
    name: "Healthcare Mobile App",
    client: "MediCare Solutions",
    status: "Testing Phase",
    progress: 90,
    tech: ["React Native", "Firebase", "TensorFlow"],
    description:
      "AI-powered health monitoring app with real-time vitals tracking",
    image:
      "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&h=600&fit=crop",
    startDate: "Nov 2024",
    expectedLaunch: "Mar 2025",
  },
  {
    id: 3,
    name: "E-commerce Platform",
    client: "Urban Marketplace",
    status: "Launched",
    progress: 100,
    tech: ["Next.js", "Stripe", "MongoDB", "Vercel"],
    description: "Multi-vendor marketplace with real-time inventory management",
    image:
      "https://images.unsplash.com/photo-1557821552-17105176677c?w=800&h=600&fit=crop",
    startDate: "Aug 2024",
    expectedLaunch: "Launched Feb 2025",
  },
  {
    id: 4,
    name: "Logistics Tracking System",
    client: "Global Freight Co.",
    status: "Planning",
    progress: 25,
    tech: ["Vue.js", "Go", "Kubernetes", "IoT"],
    description:
      "Real-time shipment tracking with predictive delivery estimates",
    image:
      "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&h=600&fit=crop",
    startDate: "Mar 2025",
    expectedLaunch: "Sep 2025",
  },
];

// Service details for expanded services page
export const serviceDetails = {
  website: {
    title: "Website Development",
    tagline: "Beautiful, Fast, Conversion-Optimized",
    description:
      "We build websites that not only look stunning but drive real business results. From corporate sites to complex web applications, our expertise ensures your digital presence stands out.",
    offerings: [
      {
        name: "Corporate Websites",
        description:
          "Professional websites that establish credibility and generate leads",
        icon: "building",
      },
      {
        name: "E-commerce Solutions",
        description:
          "Scalable online stores with seamless checkout experiences",
        icon: "shopping-cart",
      },
      {
        name: "Web Applications",
        description:
          "Custom web apps built with modern frameworks for any business need",
        icon: "code",
      },
      {
        name: "CMS Development",
        description: "Easy-to-manage content systems tailored to your workflow",
        icon: "edit",
      },
    ],
  },
  software: {
    title: "Software Development",
    tagline: "Enterprise-Grade Solutions",
    description:
      "Build robust, scalable software that powers your business. Our team architects and develops custom solutions that integrate seamlessly with your existing infrastructure.",
    offerings: [
      {
        name: "SaaS Platforms",
        description:
          "Multi-tenant software as a service with subscription management",
        icon: "cloud",
      },
      {
        name: "API Development",
        description:
          "RESTful and GraphQL APIs built for performance and security",
        icon: "share-2",
      },
      {
        name: "Legacy Modernization",
        description:
          "Migrate old systems to modern, maintainable architectures",
        icon: "refresh-cw",
      },
      {
        name: "System Integration",
        description:
          "Connect disparate systems with custom middleware and automation",
        icon: "link",
      },
    ],
  },
  app: {
    title: "App Development",
    tagline: "Native & Cross-Platform",
    description:
      "Create mobile experiences that users love. We develop high-performance apps for iOS and Android that feel native, fast, and intuitive.",
    offerings: [
      {
        name: "iOS Development",
        description: "Swift and SwiftUI apps optimized for Apple's ecosystem",
        icon: "smartphone",
      },
      {
        name: "Android Development",
        description: "Kotlin apps that leverage the latest Android features",
        icon: "tablet",
      },
      {
        name: "React Native",
        description: "Cross-platform apps with native performance",
        icon: "layers",
      },
      {
        name: "Progressive Web Apps",
        description: "Web apps that work offline and feel like native apps",
        icon: "globe",
      },
    ],
  },
  uiux: {
    title: "UI/UX Design",
    tagline: "Design That Converts",
    description:
      "Our design philosophy is simple: beautiful interfaces that solve real problems. We create user experiences that delight while driving business metrics.",
    offerings: [
      {
        name: "User Research",
        description:
          "Data-driven insights into your users' needs and behaviors",
        icon: "users",
      },
      {
        name: "Design Systems",
        description: "Scalable component libraries that ensure consistency",
        icon: "box",
      },
      {
        name: "Prototyping",
        description:
          "Interactive prototypes to validate ideas before development",
        icon: "figma",
      },
      {
        name: "Usability Testing",
        description: "Real user feedback to refine and perfect your product",
        icon: "check-circle",
      },
    ],
  },
};

// Original projects data for portfolio
export const projects = [
  {
    id: 1,
    title: "SYNAPSE AI ENGINE",
    description:
      "Neural network optimization layer for real-time predictive logistics. Developed using PyTorch, Rust, and AWS Lambda.",
    status: "Deployed",
    category: "AI & ML",
    tags: ["RUST", "PYTORCH", "AWS"],
    image:
      "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&h=800&fit=crop",
    featured: true,
    size: "large",
  },
  {
    id: 2,
    title: "VOID SHIELD",
    description:
      "Enterprise-grade encryption protocols for decentralized financial assets.",
    status: "In Progress",
    category: "Blockchain",
    tags: ["SOLIDITY", "NODE.JS"],
    image:
      "https://images.unsplash.com/photo-1639322537228-f710d846310a?w=800&h=1200&fit=crop",
    featured: false,
    size: "small",
  },
  {
    id: 3,
    title: "QUANTUM CORE",
    description:
      "Next-gen inventory management for complex global supply chains.",
    status: "Deployed",
    category: "Web Architectures",
    tags: ["GO", "REACT", "K8S"],
    image:
      "https://images.unsplash.com/photo-1518770660439-4636190af475?w=900&h=700&fit=crop",
    featured: false,
    size: "medium",
  },
  {
    id: 4,
    title: "ETHER MOBILE",
    description:
      "Native performance high-fidelity mobile application for asset tracking.",
    status: "Deployed",
    category: "Mobile Ecosystems",
    tags: ["SWIFT", "KOTLIN"],
    image:
      "https://images.unsplash.com/photo-1592478411213-6153e4ebc07d?w=900&h=700&fit=crop",
    featured: false,
    size: "medium",
  },
  {
    id: 5,
    title: "Kravion PROTOCOL",
    description:
      "Distributed microservices architecture for high-frequency trading platforms.",
    status: "Planning",
    category: "Web Architectures",
    tags: ["GOLANG", "KAFKA", "REDIS"],
    image:
      "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=900&h=700&fit=crop",
    featured: false,
    size: "medium",
  },
  {
    id: 6,
    title: "CIPHER ANALYTICS",
    description:
      "Real-time data pipeline processing 10M+ events per second with ML-driven insights.",
    status: "Deployed",
    category: "AI & ML",
    tags: ["PYTHON", "SPARK", "TENSORFLOW"],
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=900&h=700&fit=crop",
    featured: false,
    size: "medium",
  },
];

export const categories = [
  "All Projects",
  "AI & ML",
  "Web Architectures",
  "Mobile Ecosystems",
  "Blockchain",
];
